import type { AppliedPayeeValues } from '@/components/ReportForm/InstantPayeeSwitcher/hooks/useChangePayeeValue';
import { TOTAL_AMOUNT_TARGET_KEY } from '@/context/services/ap_mismatch_alert_settings/utils';
import { useGetApPayeeBankDetailsWithStatus } from '@/context/services/ap_payee/ApPayee.service';
import { useGetReportInferences } from '@/context/services/report_inferences/ReportInferences.service';
import {
  toAppliedDetail,
  toComparableDueDate,
  type ApReportInference,
  type AppliedInference,
  type InferenceEntry,
} from '@/context/services/report_inferences/utils';
import { useOcrContext } from '@/features/InvoiceReport/Edit/components/Context/OcrContext';
import {
  useInferenceAlertState,
  usePayeeBankInferenceAlertState,
} from '@/features/InvoiceReport/Edit/components/hooks/useInferenceAlertState';
import { useInferenceBaseline } from '@/features/InvoiceReport/Edit/components/hooks/useInferenceBaseline';
import { type InferenceAlerts } from '@/features/InvoiceReport/Edit/components/ReportFormInputs/ReportFormInputs';
import { PaymentRequestForm } from '@/features/InvoiceReport/Edit/type';
import { useDataExtractionMismatchAlertFlag } from '@/hooks/feature_flag/useDataExtractionMismatchAlertFlag';
import { useDataExtractionMismatchAlertPhase2Flag } from '@/hooks/feature_flag/useDataExtractionMismatchAlertPhase2Flag';
import { useMismatchAlert } from '@/hooks/invoice_reports/useMismatchAlert';
import { useCallback, useEffect } from 'react';
import { useWatch, type Control, type UseFormGetValues } from 'react-hook-form';

type UseReportInferenceAlertsParams = {
  reportId: string;
  reportTypeId: string;
  control: Control<PaymentRequestForm>;
  getValues: UseFormGetValues<PaymentRequestForm>;
  payeeInputId?: string;
  dueDateInputId?: string;
  invoiceRegistrationNumberId?: string;
  isActiveInstantPayee?: boolean;
};

const NO_INFERENCES: ApReportInference[] = [];

export type UseReportInferenceAlertsReturn = {
  inferenceAlerts: InferenceAlerts;
  totalAmountInference: InferenceEntry | undefined;
  isTotalAmountAlertEnabled: boolean;
  applyPayeeDerivedBaseline: (applied?: AppliedPayeeValues) => void;
  appliedInferences: AppliedInference[];
};

export const useReportInferenceAlerts = ({
  reportId,
  reportTypeId,
  control,
  getValues,
  payeeInputId,
  dueDateInputId,
  invoiceRegistrationNumberId,
  isActiveInstantPayee,
}: UseReportInferenceAlertsParams): UseReportInferenceAlertsReturn => {
  const { reflectedInferences } = useOcrContext();

  const isMismatchAlertFlag = useDataExtractionMismatchAlertFlag();
  const isMismatchAlertPhase2Flag = useDataExtractionMismatchAlertPhase2Flag();
  const rawReportInferences = useGetReportInferences(
    reportId,
    isMismatchAlertFlag
  );
  const inferences =
    reflectedInferences ?? rawReportInferences?.inferences ?? NO_INFERENCES;

  const { baseline, appliedInferences, applyBaselineOverrides } =
    useInferenceBaseline(inferences);
  const { inferencesMap, isFieldAlertEnabled } = useMismatchAlert(
    baseline,
    reportTypeId,
    { isSpotPayee: !!isActiveInstantPayee }
  );

  /**
   * dueData that is derived from payee rule is treated as the baseline for subsequent comparisons.
   * If it is not derived (i.e. "payee only"), the previous baseline value is retained as-is.
   */
  const baselineDueDate = baseline[0]?.due_date;
  const applyPayeeDerivedBaseline = useCallback(
    (applied?: AppliedPayeeValues) => {
      if (!isMismatchAlertFlag || !isMismatchAlertPhase2Flag) return;
      const value = toComparableDueDate(applied?.dueDate);
      if (!value) return;
      applyBaselineOverrides({
        due_date: toAppliedDetail(baselineDueDate, {
          value,
          source: 'system',
          sourceDetail: 'payee',
        }),
      });
    },
    [
      isMismatchAlertFlag,
      isMismatchAlertPhase2Flag,
      applyBaselineOverrides,
      baselineDueDate,
    ]
  );

  const payeeFormValue = useWatch({ control, name: 'payee.value' });
  const selectedPayeeId = payeeFormValue || undefined;
  const isPayeeAlertEnabled =
    !!payeeInputId &&
    !isActiveInstantPayee &&
    isFieldAlertEnabled(payeeInputId);
  // Masking drops apPayee only. Bank name / branch still need the selected
  // payee's registered details for comparison.
  const shouldLookupPayeeBankDetails =
    isPayeeAlertEnabled &&
    !!(
      inferencesMap.apPayee ||
      inferencesMap.payeeBankName ||
      inferencesMap.payeeBankBranch
    );

  const {
    accountNumber: selectedPayeeAccountNumber,
    bankName: selectedPayeeBankName,
    bankBranch: selectedPayeeBankBranch,
    isLoading: isSelectedPayeeBankDetailsLoading,
  } = useGetApPayeeBankDetailsWithStatus(
    shouldLookupPayeeBankDetails ? selectedPayeeId : undefined
  );

  const getPayeeCurrentValues = useCallback(
    () => ({
      accountNumber: selectedPayeeAccountNumber,
      bankName: selectedPayeeBankName,
      bankBranch: selectedPayeeBankBranch,
    }),
    [selectedPayeeAccountNumber, selectedPayeeBankName, selectedPayeeBankBranch]
  );
  const getDueDateComparableValue = useCallback(
    () => toComparableDueDate(getValues('payee.dueDate')),
    [getValues]
  );
  const getInvoiceRegNumComparableValue = useCallback(() => {
    if (!invoiceRegistrationNumberId) return null;
    const value = getValues('reportForm')?.[invoiceRegistrationNumberId];
    return typeof value === 'string' ? value : null;
  }, [getValues, invoiceRegistrationNumberId]);

  const payee = usePayeeBankInferenceAlertState(
    {
      bankName: inferencesMap.payeeBankName,
      bankBranch: inferencesMap.payeeBankBranch,
      accountNumber: inferencesMap.apPayee,
    },
    getPayeeCurrentValues,
    isPayeeAlertEnabled && !isSelectedPayeeBankDetailsLoading
  );

  const { onBlur: recomputePayeeAlert } = payee;
  useEffect(() => {
    recomputePayeeAlert();
  }, [
    recomputePayeeAlert,
    selectedPayeeAccountNumber,
    selectedPayeeBankName,
    selectedPayeeBankBranch,
    isSelectedPayeeBankDetailsLoading,
  ]);

  const dueDate = useInferenceAlertState(
    inferencesMap.dueDate,
    getDueDateComparableValue,
    !!dueDateInputId && isFieldAlertEnabled(dueDateInputId)
  );
  const invoiceRegistrationNumber = useInferenceAlertState(
    inferencesMap.invoiceRegistrationNumber,
    getInvoiceRegNumComparableValue,
    !!invoiceRegistrationNumberId &&
      isFieldAlertEnabled(invoiceRegistrationNumberId)
  );

  return {
    inferenceAlerts: { payee, dueDate, invoiceRegistrationNumber },
    totalAmountInference: inferencesMap.totalAmount,
    isTotalAmountAlertEnabled: isFieldAlertEnabled(TOTAL_AMOUNT_TARGET_KEY),
    applyPayeeDerivedBaseline,
    appliedInferences,
  };
};
